// Figma-exported assets for the MetalCloud page, hosted on the NowPurchase CDN.
const CDN = "https://images.nowpurchase.com/landing";

// Transparent outside the hexagon — it sits on the tinted band, not on white.
export const HERO_HEXAGON = `${CDN}/20260928101504_hero-hex-t.webp`;

// Served from this app's own /public so the Lottie player can fetch() them.
// The CDN does not send Access-Control-Allow-Origin, which blocks fetch (though
// not <img>), so these cannot be loaded cross-origin.
export const LOTTIE = {
  scatteredDataDesktop: "/lottie/scattered-data-desktop.json",
  scatteredDataMobile: "/lottie/scattered-data-mobile.json",
  metalcloudWayDesktop: "/lottie/metalcloud-way-desktop.json",
  metalcloudWayMobile: "/lottie/metalcloud-way-mobile.json",
};

// All logos are trimmed with their transparency intact. They sit on both white
// cards (Trusted Partners) and #E8F2F8 cards (Testimonials, Recognized
// Globally), so a flattened white backdrop would show as a box on the tinted
// ones.
export const PARTNER_LOGOS = [
  // `mobile` is the logo's own box in the Figma mobile grid (12231:4092),
  // as a percentage of the tile's content area — each logo is sized by hand
  // there rather than sharing one height. `mobileOrder` reorders the last two:
  // desktop runs ...Synergy, RKFL; mobile runs ...RKFL, Synergy (full width).
  {
    name: "Titagarh Rail Systems",
    src: `${CDN}/20260928141750_a-logo-titagarh.webp`,
    mobile: { width: "83.8%", height: "43.9%" },
  },
  {
    name: "Nelcast Limited",
    src: `${CDN}/20260928141751_a-logo-nelcast.webp`,
    mobile: { width: "100%", height: "100%" },
  },
  {
    name: "Brakes India",
    src: `${CDN}/20260928141751_a-logo-brakes-india.webp`,
    mobile: { width: "77.7%", height: "76.8%" },
  },
  {
    name: "Synergy Green Industries",
    src: `${CDN}/20260928141752_a-logo-synergy-green.webp`,
    mobile: { width: "96.1%", height: "95.6%" },
    mobileOrder: 1,
    mobileFullWidth: true,
  },
  {
    name: "Ramkrishna Forgings",
    src: `${CDN}/20260928141752_a-logo-rkfl.webp`,
    mobile: { width: "91.6%", height: "92.5%" },
    mobileOrder: 0,
  },
];

// Section: Recognized Globally. Each logo is cropped out of its Figma source
// composite (which also contained the caption text) so the captions can be real
// text rather than baked into the image.
export const AWARDS = [
  {
    id: "wfo",
    logo: `${CDN}/20260928141753_a-wfo.webp`,
    alt: "World Foundry Organization",
    logoWidth: 41,
    lines: [
      { text: "Ranked in the Top 10 WFO", bold: true },
      { text: "Young Research Confrence 2024" },
      { text: "For MetalCloud Paper" },
    ],
  },
  {
    id: "ft",
    logo: `${CDN}/20260928141753_a-ft.webp`,
    alt: "Financial Times",
    logoWidth: 45,
    lines: [
      { text: "Top 500 High-Growth Companies", bold: true },
      { text: "In the Asia-Pacific Region" },
      { text: "2023, 2024 & 2025" },
    ],
  },
  {
    id: "deloitte",
    logo: `${CDN}/20260928141754_a-deloitte.webp`,
    alt: "Deloitte Technology Fast 50 India",
    logoOnly: true,
    logoHeight: 61,
  },
  {
    id: "dnb",
    logo: `${CDN}/20260928141754_a-dnb.webp`,
    alt: "dun & bradstreet",
    caption: "Startup50 2023 Awards",
    logoHeight: 39,
  },
  {
    id: "forbes",
    logo: `${CDN}/20260928074259_award-forbes.svg`,
    alt: "Forbes Asia 100 To Watch",
    logoWidth: 87,
    lines: [
      { text: "One of Asia-Pacific’s" },
      { text: "Top 100 Emerging Companies 2026", bold: true },
    ],
  },
  {
    id: "afs",
    logo: `${CDN}/20260928074258_award-afs.svg`,
    alt: "American Foundry Society corporate member",
    logoOnly: true,
    logoHeight: 93,
  },
  {
    id: "nffs",
    logo: `${CDN}/20260928141755_a-nffs.webp`,
    alt: "Non-Ferrous Founders’ Society",
    caption: "Proud Member Of",
    logoHeight: 54,
  },
  {
    id: "iif",
    logo: `${CDN}/20260928141755_a-iif.webp`,
    alt: "The Institute of Indian Foundrymen",
    caption: "Proud Member Of",
    logoHeight: 66,
  },
];

// Section: Built in India. A single composite export — the Figma group nests
// 40+ per-pin client logos. Its background is transparent: the section is
// #D0E4F2 on desktop but #E8F2F8 on mobile, and an earlier version had the
// desktop colour baked in, which showed as a hard seam under the copy.
export const INDIA_MAP = `${CDN}/20260929060849_india-map-t.webp`;

// Section: Diagnostic Report.
// Opens from the floating badge on the Diagnostic laptop render.
export const DIAGNOSTIC_REPORT_URL =
  "https://generi-internal-diagnostic.lovable.app/";

export const DIAGNOSTIC = {
  laptop: `${CDN}/20260928074718_diag-laptop.webp`,
  folderChart: `${CDN}/20260928074719_icon-folderchart.svg`,
};

// The icons' outer white disc is authored as 20% white with
// `mix-blend-mode: plus-lighter`, which over the #D0E4F2 card saturates to solid
// white. An SVG loaded through <img> cannot blend with the page behind it, so
// that result is baked into these copies as an opaque fill.
export const DIAGNOSTIC_FEATURES = [
  {
    id: "onsite",
    icon: `${CDN}/20260928143755_w-icon-onsite.svg`,
    title: ["On-site", "Visit"],
    body: "On-site factory visit by process control & data engineer",
  },
  {
    id: "deepdive",
    icon: `${CDN}/20260928143756_w-icon-deepdive.svg`,
    title: "Process Deep Dive",
    body: "End-to-end process deep dive with improvement insights",
  },
  {
    id: "benchmark",
    icon: `${CDN}/20260928143757_w-icon-benchmark.svg`,
    title: "Foundry Benchmark",
    body: "Benchmark vs top-performing foundries",
  },
  {
    id: "cost",
    icon: `${CDN}/20260928143757_w-icon-cost.svg`,
    title: ["Cost", "Analysis"],
    body: "Detailed section-wise cost analysis",
  },
];

// Section: Testimonials.
export const QUOTE_MARK = `${CDN}/20260928074852_quote.svg`;

export const TESTIMONIALS = [
  {
    id: "titagarh",
    quote:
      "Managing operations at this scale requires strong control and transparency. MetalCloud makes the entire production flow traceable with real-time alerts and automated MIS.",
    name: "Anil Agarwal",
    role: "Dy. MD & CEO",
    logo: `${CDN}/20260928141750_a-logo-titagarh.webp`,
    logoAlt: "Titagarh Rail Systems Ltd.",
    logoWidth: 237,
  },
  {
    id: "brakes-india",
    quote:
      "The software's advanced features enable us to make the perfect charge mix, achieving target chemistry in the first attempt, and works efficiently by reducing unnecessary addition of raw materials and efforts.",
    name: "C. Kannan",
    role: "Manager Manufacturing",
    logo: `${CDN}/20260928141751_a-logo-brakes-india.webp`,
    logoAlt: "Brakes India Pvt. Ltd.",
    logoWidth: 221,
  },
  {
    // Third card: only the mobile frame (12233:43789) lays this one out — the
    // desktop frame shows two and parks the rest in an unbuilt carousel track.
    id: "crescent",
    quote:
      "As a foundry owner, I highly recommend this procurement service. They provide high-quality materials at competitive prices, and their expertise has helped me streamline my procurement processes. Their dedication to customer service is exceptional. Thank you!",
    name: "Sandip Poddar",
    role: "Owner",
    avatar: `${CDN}/20260929054612_t3-avatar.webp`,
    logo: `${CDN}/20260929054612_t3-crescent.webp`,
    logoAlt: "Crescent Foundry",
    logoWidth: 117,
    mobileOnly: true,
  },
];

// Section: Titagarh case study.
export const CASE_STUDY_HERO = `${CDN}/20260928075103_cs-hero.webp`;

export const CASE_STUDY_STATS = [
  {
    id: "yield",
    icon: `${CDN}/20260928075104_cs-icon-yield.webp`,
    title: "+3% Yield",
    // Figma sets this as two text lines, not a wrap.
    body: ["30% reduction in manpower", "requirement"],
  },
  {
    id: "realtime",
    icon: `${CDN}/20260928143758_cs-w-icon-realtime.svg`,
    title: "Real-Time Tech",
    body: "Direct suggestions for EAF, optimizing operations.",
  },
  {
    id: "decisions",
    icon: `${CDN}/20260928143758_cs-w-icon-decisions.svg`,
    title: "50% Faster Decisions",
    body: "Quick, efficient decision-making with advanced melting modules.",
  },
  {
    id: "supply",
    icon: `${CDN}/20260928143758_cs-w-icon-supply.svg`,
    title: "All-In-One Supply",
    body: "Comprehensive sourcing for all raw materials, including new product trials.",
  },
];
